import { useCallback, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import * as imagePreviewActions from '@/shared/modules/imagePreview/actions';
import config from '@/config';
import {
	LANE_CHOOSER_THEME_QUERY_PARAM,
	getGameTypeFromSearchParams,
	type SupportedGameType,
} from '@/features/gameSession/queryParams';
import * as gameActions from '../store/actions';
import { GAME_TYPES } from '../store/constants';
import { GameAnswerEvent, GameCloseEvent, GameData, GameFinishEvent } from '../types';

import LaneChooserGame from '@/features/laneChooser/components/LaneChooserGame';
import LetterPartyGame from '@/features/letterParty/components/LetterPartyGame';
import TimeGame from '@/features/timeGame/components/TimeGame';

import type { RootState } from '@/rootState';
import type { GameAudioProps } from '../types';

export type GameWrapperProps = {
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
} & GameAudioProps;

const GameWrapper = ({ onAnswer, onFinish, audioOn, onAudioChange }: GameWrapperProps) => {
	const dispatch = useDispatch<any>();
	const location = useLocation();
	const requestedGameTypeRef = useRef<SupportedGameType | null>(null);
	const {
		gameData: storeGameData,
		loading,
		questions: storeQuestions,
	} = useSelector(
		(state: RootState) => ({
			gameData: state.gameReducer.gameData,
			loading: state.gameReducer.loading,
			questions: state.gameReducer.questions,
		}),
		shallowEqual,
	);
	const gameData = storeGameData;
	const questions = storeQuestions ?? [];

	const { game } = gameData || {};

	const requestedGameType = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);

		return getGameTypeFromSearchParams(searchParams);
	}, [location.search]);
	const laneChooserThemeKey = useMemo(() => {
		return new URLSearchParams(location.search).get(LANE_CHOOSER_THEME_QUERY_PARAM);
	}, [location.search]);
	const selectedGameType = requestedGameType ?? game?.type;
	const canRequestGameData =
		requestedGameType != null || Boolean(config.data_href?.href) || config.use_dummy_game_data;
	const freePlayCategoryId = useMemo(() => {
		const categoryIdFromQuery = new URLSearchParams(location.search).get('category_id');

		if (categoryIdFromQuery != null && !Number.isNaN(Number(categoryIdFromQuery))) {
			return Number(categoryIdFromQuery);
		}

		return undefined;
	}, [location.search]);

	useEffect(() => {
		document.documentElement.classList.add('game-active');
		return () => {
			document.documentElement.classList.remove('game-active');
			dispatch(gameActions.clearGameState());
		};
	}, [dispatch]);

	useEffect(() => {
		if (!canRequestGameData) {
			return;
		}

		const requestedGameTypeChanged = requestedGameTypeRef.current !== requestedGameType;

		if (!questions.length || requestedGameTypeChanged) {
			requestedGameTypeRef.current = requestedGameType;
			dispatch(gameActions.getGameDataRequest());
		}
	}, [canRequestGameData, dispatch, questions.length, requestedGameType]);

	const handleFinish = useCallback(
		(event: GameFinishEvent) => {
			dispatch(
				gameActions.finishGameRequest(
					gameData?.id,
					freePlayCategoryId,
					event.gameOutcome,
					event.score,
					event.data,
				),
			);

			onFinish?.(event);
		},
		[dispatch, freePlayCategoryId, gameData?.id, onFinish],
	);

	const handleAnswer = useCallback(
		(event: GameAnswerEvent) => {
			onAnswer?.(event);

			if (event.finishEvent) {
				handleFinish(event.finishEvent);
			}
		},
		[handleFinish, onAnswer],
	);

	const handleClose = useCallback(
		(event?: GameCloseEvent) => {
			if (event?.finishEvent) {
				handleFinish(event.finishEvent);
			}

			if (config.exit_url) {
				window.location.href = config.exit_url;
			}
		},
		[handleFinish],
	);

	const handleReplay = useCallback(
		(nextGameData: GameData) => {
			dispatch(gameActions.getGameDataSuccess(nextGameData));
		},
		[dispatch],
	);

	const handlePreviewImage = useCallback(
		(imageSrc: string) => {
			dispatch(
				imagePreviewActions.setImagePreviewModal({
					open: true,
					imageSrc,
				}),
			);
		},
		[dispatch],
	);

	if (loading || requestedGameTypeRef.current !== requestedGameType || !questions.length) {
		return null;
	}

	if (selectedGameType === GAME_TYPES.LANE_CHOOSER_GAME) {
		return (
			<LaneChooserGame
				gameData={gameData}
				questions={questions}
				themeKey={laneChooserThemeKey}
				onAnswer={handleAnswer}
				onFinish={handleFinish}
				onClose={handleClose}
				onPreviewImage={handlePreviewImage}
				audioOn={audioOn}
				onAudioChange={onAudioChange}
			/>
		);
	}

	if (selectedGameType === GAME_TYPES.LETTER_PARTY_GAME) {
		return (
			<LetterPartyGame
				key={gameData?.id ?? 'letter-party'}
				gameData={gameData}
				questions={questions}
				onAnswer={handleAnswer}
				onFinish={handleFinish}
				onClose={handleClose}
				onReplay={handleReplay}
				audioOn={audioOn}
				onAudioChange={onAudioChange}
			/>
		);
	}

	if (selectedGameType === GAME_TYPES.TIME_GAME) {
		return (
			<TimeGame
				key={gameData?.id ?? 'time-game'}
				gameData={gameData}
				questions={questions}
				onAnswer={handleAnswer}
				onFinish={handleFinish}
				onClose={handleClose}
				onReplay={handleReplay}
				audioOn={audioOn}
				onAudioChange={onAudioChange}
			/>
		);
	}

	// Default to TimeGame in standalone mode
	return (
		<TimeGame
			key={gameData?.id ?? 'time-game'}
			gameData={gameData}
			questions={questions}
			onAnswer={handleAnswer}
			onFinish={handleFinish}
			onClose={handleClose}
			onReplay={handleReplay}
			audioOn={audioOn}
			onAudioChange={onAudioChange}
		/>
	);
};

export default GameWrapper;
